import { Check, ChevronDown, ExternalLink, FileText, Layers, Workflow } from "lucide-react";
import { useState } from "react";
import { AccountAvatar } from "@/components/brand/brand";
import { Badge } from "@/components/ui/badge";
import { SearchInput } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/misc";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/overlays";
import { PanelSection, Peek, PeekCard, QuickPanel } from "@/components/ui/quick-view";
import { ErrorState } from "@/components/ui/states";
import { useAccounts } from "@/hooks/data";
import { useApiQuery } from "@/lib/api/hooks";
import { fmtCompact, fmtDateTime, fmtRelative } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { usePeek } from "@/lib/peek";
import type { Post, PostRef } from "@/lib/types";
import { cn } from "@/lib/utils";
import { ChannelImage, MediaCopyingChip } from "./channel-image";
import { metric, postStatus, STATUS_TONE } from "./lib";
import { EditedOnInstagramBadge, ReloadPostButton } from "./reload";

/** The minimum a thumbnail needs: works for a PostRef (runs/approvals) and a full Post. */
type ThumbSource = Pick<PostRef, "postId" | "caption" | "thumbnailUrl">;
export const refOfPost = (p: Post): PostRef => ({
  postId: p.id,
  publicationId: p.publications[0]?.id ?? null,
  accountId: p.publications[0]?.accountId ?? null,
  caption: p.caption,
  thumbnailUrl: p.media[0]?.url ?? null,
  permalink: p.publications.find((x) => x.permalink)?.permalink ?? null,
});

/**
 * A post thumbnail that peeks the post on hover/focus and opens the post QuickPanel on click/Enter.
 * `label` false = decorative inside a bigger control (the parent names it).
 */
export function PostThumb({ post, size = 40, onOpen, className, peek = true }: { post: ThumbSource; size?: number; onOpen?: () => void; className?: string; peek?: boolean }) {
  const { t } = useT();
  const peekApi = usePeek();
  const open = onOpen ?? (() => peekApi.open("post", post.postId));
  const img = (
    <button
      type="button"
      onClick={open}
      aria-label={`${t("quick.post.open")}: ${post.caption.slice(0, 60)}`}
      data-testid="post-thumb"
      data-post-id={post.postId}
      className={cn("relative shrink-0 overflow-hidden rounded-control border border-line bg-surface-2 outline-none transition hover:ring-2 hover:ring-accent/30 focus-visible:shadow-[var(--ring)]", className)}
      style={{ width: size, height: size }}
    >
      {post.thumbnailUrl ? <ChannelImage src={post.thumbnailUrl} postId={post.postId} compact className="size-full" /> : <FileText className="m-auto size-4 text-ink-3" />}
    </button>
  );
  if (!peek) return img;
  return (
    <Peek label={t("quick.post.peek")} content={() => <PostPeekBody postId={post.postId} fallback={post} />} onActivate={open} side="top">
      {img}
    </Peek>
  );
}

/** Peek card content: loads the post lazily (cached), shows media, caption, status and headline numbers. */
export function PostPeekBody({ postId, fallback }: { postId: string; fallback?: ThumbSource }) {
  const { t } = useT();
  const q = useApiQuery("post", { params: { postId } }, { staleTime: 60_000 });
  const { data: accounts = [] } = useAccounts();
  const p = q.data;
  const pub = p?.publications.find((x) => x.status === "published") ?? p?.publications[0];
  const acc = accounts.find((a) => a.id === pub?.accountId);
  const first = p?.media[0] ?? null;
  return (
    <PeekCard
      media={
        <div className="relative aspect-[16/10] bg-surface-2">
          <ChannelImage media={first} src={first ? null : (fallback?.thumbnailUrl ?? null)} postId={postId} className="size-full" />
        </div>
      }
      title={
        <span className="flex items-center gap-2">
          {acc && <AccountAvatar account={acc} size={18} />}
          <span className="truncate" dir="ltr">
            {acc ? `‎@${acc.username}` : t("quick.post.title")}
          </span>
        </span>
      }
      meta={
        p ? (
          <>
            <Badge size="xs" tone={STATUS_TONE[postStatus(p)]}>
              {t(`content.status.${postStatus(p)}`)}
            </Badge>
            {pub?.publishedAt ? <span>{t("quick.post.publishedAgo", { when: fmtRelative(pub.publishedAt) })}</span> : pub?.scheduledAt ? <span>{t("quick.post.scheduledFor", { when: fmtDateTime(pub.scheduledAt) })}</span> : null}
          </>
        ) : q.isPending ? (
          <Skeleton className="h-3 w-24" />
        ) : null
      }
    >
      <p className="line-clamp-3 text-[12.5px] leading-relaxed text-ink-2" dir="auto">
        {p?.caption ?? fallback?.caption ?? ""}
      </p>
      {pub && pub.metrics.length > 0 && (
        <dl className="grid grid-cols-3 gap-1.5 pt-1">
          {(["reach", "likes", "comments"] as const).map((k) => (
            <div key={k} className="rounded-control bg-surface-2 px-2 py-1.5">
              <dt className="truncate text-[10.5px] text-ink-3">{t(`common.metrics.${k}`)}</dt>
              <dd className="num text-[13px] font-semibold text-ink">{fmtCompact(k === "likes" ? (pub.likesCount ?? metric(pub, k)) : k === "comments" ? pub.commentsCount : metric(pub, k))}</dd>
            </div>
          ))}
        </dl>
      )}
    </PeekCard>
  );
}

/**
 * Filter control: pick a post by its thumbnail (searchable). `posts` are the candidates (e.g. posts that
 * have pending approvals); `counts` optionally shows how many items each post has.
 */
export function PostPicker({
  posts,
  value,
  onChange,
  counts,
  anyLabel,
  label,
  testId = "post-picker",
}: {
  posts: PostRef[];
  value: string | undefined;
  onChange: (postId: string | undefined) => void;
  counts?: Map<string, number>;
  anyLabel: string;
  label: string;
  testId?: string;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = posts.find((p) => p.postId === value);
  const list = posts.filter((p) => !q.trim() || p.caption.toLowerCase().includes(q.trim().toLowerCase()));
  const choose = (id: string | undefined) => {
    onChange(id);
    setOpen(false);
    setQ("");
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          aria-label={`${label}: ${selected ? selected.caption.slice(0, 40) : anyLabel}`}
          className={cn(
            "inline-flex h-8 max-w-[240px] items-center gap-2 rounded-control border bg-surface px-2 text-[13px] text-ink outline-none transition focus-visible:shadow-[var(--ring)]",
            selected ? "border-accent/50" : "border-line hover:border-line-2",
          )}
        >
          {selected ? (
            <>
              <span className="size-6 shrink-0 overflow-hidden rounded-[6px] bg-surface-2">{selected.thumbnailUrl && <ChannelImage src={selected.thumbnailUrl} postId={selected.postId} compact className="size-full" />}</span>
              <span className="truncate" dir="auto">
                {selected.caption}
              </span>
            </>
          ) : (
            <>
              <FileText className="size-4 text-ink-3" />
              <span className="text-ink-2">{anyLabel}</span>
            </>
          )}
          <ChevronDown className="ms-auto size-3.5 shrink-0 text-ink-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[340px] p-2">
        <SearchInput value={q} onChange={setQ} placeholder={t("quick.approvals.searchPosts")} inputSize="sm" className="mb-2" />
        <ul role="listbox" aria-label={label} className="max-h-[320px] space-y-0.5 overflow-y-auto">
          <li>
            <button type="button" role="option" aria-selected={!value} onClick={() => choose(undefined)} className="flex w-full items-center gap-2.5 rounded-control px-2 py-1.5 text-start text-[13px] text-ink-2 hover:bg-surface-2">
              <span className="grid size-10 place-items-center rounded-control bg-surface-2">
                <Layers className="size-4 text-ink-3" />
              </span>
              {anyLabel}
              {!value && <Check className="ms-auto size-4 text-accent" />}
            </button>
          </li>
          {list.map((p) => (
            <li key={p.postId}>
              <button type="button" role="option" aria-selected={p.postId === value} data-testid="post-picker-option" data-post-id={p.postId} onClick={() => choose(p.postId)} className="flex w-full items-center gap-2.5 rounded-control px-2 py-1.5 text-start hover:bg-surface-2">
                <span className="size-10 shrink-0 overflow-hidden rounded-control bg-surface-2">{p.thumbnailUrl ? <ChannelImage src={p.thumbnailUrl} postId={p.postId} compact className="size-full" /> : <FileText className="m-auto mt-3 size-4 text-ink-3" />}</span>
                <span className="line-clamp-2 min-w-0 flex-1 text-[12.5px] leading-snug text-ink" dir="auto">
                  {p.caption}
                </span>
                {counts?.get(p.postId) ? <Badge size="xs" mono>{counts.get(p.postId)}</Badge> : null}
                {p.postId === value && <Check className="size-4 shrink-0 text-accent" />}
              </button>
            </li>
          ))}
          {list.length === 0 && <li className="px-2 py-4 text-center text-[12.5px] text-ink-3">{t("quick.approvals.noPosts")}</li>}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

export function PostQuickPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useT();
  const { open } = usePeek();
  const q = useApiQuery("post", { params: { postId: id } });
  const { data: accounts = [] } = useAccounts();
  const p = q.data;
  const published = p?.publications.find((x) => x.status === "published");
  const rules = useApiQuery("rules", { query: { publicationId: published?.id ?? p?.publications[0]?.id ?? "", ...(p?.publications[0]?.accountId ? { accountId: p.publications[0].accountId } : {}) } }, { enabled: !!p?.publications[0] });
  return (
    <QuickPanel
      open
      onOpenChange={(v) => !v && onClose()}
      testId="post-panel"
      icon={<FileText />}
      loading={q.isPending}
      title={t("quick.post.title")}
      subtitle={p ? <Badge tone={STATUS_TONE[postStatus(p)]}>{t(`content.status.${postStatus(p)}`)}</Badge> : undefined}
      fullPage={{ to: "/content/posts/$postId", params: { postId: id } }}
    >
      {q.isPending ? (
        <Skeleton className="aspect-square w-full rounded-card" />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} compact />
      ) : p ? (
        <>
          <div className="mb-4 grid grid-cols-3 gap-1.5 overflow-hidden rounded-card">
            {p.media.slice(0, 3).map((m, i) => (
              <div key={m.id} className={cn("relative bg-surface-2", p.media.length === 1 ? "col-span-3 aspect-[4/3]" : i === 0 ? "col-span-2 row-span-2 aspect-square" : "aspect-square")}>
                <ChannelImage media={m} postId={p.id} compact={i > 0} className="absolute inset-0" testId="post-panel-media" />
                {i === 0 && <MediaCopyingChip status={p.mediaStatus} className="absolute bottom-2 start-2" />}
              </div>
            ))}
          </div>
          {(p.editedOnChannelAt || p.publications.some((x) => x.status === "published" && x.externalId)) && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <EditedOnInstagramBadge post={p} />
              <ReloadPostButton post={p} size="xs" />
            </div>
          )}
          <PanelSection title={t("quick.post.caption")}>
            <p className="whitespace-pre-line text-[13.5px] leading-relaxed text-ink" dir="auto">
              {p.caption}
            </p>
          </PanelSection>
          <PanelSection title={t("quick.post.publications")}>
            <ul className="space-y-2">
              {p.publications.map((pb) => {
                const acc = accounts.find((a) => a.id === pb.accountId);
                return (
                  <li key={pb.id} className="flex items-center gap-2.5 rounded-field border border-line p-2.5">
                    {acc && <AccountAvatar account={acc} size={26} />}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-medium text-ink" dir="ltr">
                        &lrm;@{acc?.username ?? "—"}
                      </div>
                      <div className="text-[11.5px] text-ink-3">
                        {pb.publishedAt ? t("quick.post.publishedAgo", { when: fmtRelative(pb.publishedAt) }) : pb.scheduledAt ? t("quick.post.scheduledFor", { when: fmtDateTime(pb.scheduledAt) }) : t(`common.postStatus.${pb.status}`)}
                        {pb.status === "published" && <> · {t("quick.post.comments", { count: pb.commentsCount })}</>}
                      </div>
                    </div>
                    {pb.permalink && (
                      <a href={pb.permalink} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 text-[12px] font-medium text-accent hover:underline">
                        {t("quick.post.viewOnInstagram")}
                        <ExternalLink className="size-3" />
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          </PanelSection>
          {p.publications[0] && (
            <PanelSection title={t("quick.post.rules")}>
              {rules.isPending ? (
                <Skeleton className="h-12 rounded-field" />
              ) : (rules.data ?? []).length === 0 ? (
                <p className="text-[12.5px] text-ink-3">{t("quick.post.noRules")}</p>
              ) : (
                <ul className="space-y-1.5">
                  {(rules.data ?? []).map((r) => (
                    <li key={r.id}>
                      <button type="button" onClick={() => open("rule", r.id)} className="flex w-full items-center gap-2.5 rounded-field border border-line p-2 text-start transition hover:border-line-2 hover:bg-surface-2/60">
                        <span className="grid size-8 shrink-0 place-items-center rounded-control text-[15px]" style={{ background: `color-mix(in srgb, ${r.color ?? "var(--accent)"} 16%, transparent)` }} aria-hidden>
                          {r.icon ?? <Workflow className="size-4" />}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-ink">{r.name}</span>
                        <Badge size="xs" tone={r.isActive ? "ok" : "neutral"}>
                          {r.isActive ? t("common.states.on") : t("common.states.off")}
                        </Badge>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </PanelSection>
          )}
        </>
      ) : null}
    </QuickPanel>
  );
}
